// ParticleSimulator.jsx
import React, { useRef, useState, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

/* ----------------------- Inline CSS ----------------------- */
const styles = `
.particle-root {
  font-family: "Inter", monospace;
  color: #0ff;
  background: radial-gradient(circle at 50% 20%, #050505 0%, #000 80%);
  padding: 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

h2 {
  color: #0ff;
  font-size: 1.5rem;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
  text-shadow: 0 0 5px #0ff;
}

.controls {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  margin-bottom: 18px;
  align-items: center;
  max-width: 900px;
}

.controls label {
  display: flex;
  flex-direction: column;
  color: #0f0;
  font-size: 13px;
  text-align: center;
}

.controls input[type="range"],
.controls select {
  width: 180px;
  accent-color: #0f0;
  cursor: pointer;
}

.canvas-wrap {
  width: 90%;
  max-width: 1000px;
  height: 600px;
  border: 1px solid #0ff;
  border-radius: 12px;
  background: linear-gradient(180deg, #050505 0%, #000 100%);
  box-shadow: 0 0 25px rgba(0,255,255,0.2);
  overflow: hidden;
  margin-bottom: 10px;
}

.legend {
  margin-top: 10px;
  color: #9f9;
  font-size: 13px;
}

.button {
  background: #0f0;
  color: #000;
  border: none;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: bold;
  box-shadow: 0 0 8px #0ff;
  transition: all 0.2s ease;
}

.button:hover {
  background: #5ff;
  transform: scale(1.05);
}
`;

/* ----------------------- Utility ----------------------- */
const rand = (a, b) => a + Math.random() * (b - a);

function createParticles({ nElectrons, nProtons, nQuarks }) {
  const pts = [];
  let id = 0;

  // electrons
  for (let i = 0; i < nElectrons; i++) {
    pts.push({
      id: id++,
      type: "electron",
      charge: -1,
      mass: 1,
      pos: new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-1, 1)),
      vel: new THREE.Vector3(rand(-0.005, 0.005), rand(-0.005, 0.005), rand(-0.005, 0.005)),
      mesh: null,
    });
  }

  // protons
  for (let i = 0; i < nProtons; i++) {
    pts.push({
      id: id++,
      type: "proton",
      charge: +1,
      mass: 1836,
      pos: new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-1, 1)),
      vel: new THREE.Vector3(rand(-0.002, 0.002), rand(-0.002, 0.002), rand(-0.002, 0.002)),
      mesh: null,
    });
  }

  // quarks grouped
  const quarkGroups = Math.max(1, Math.floor(nQuarks / 3));
  let qCount = 0;
  for (let g = 0; g < quarkGroups; g++) {
    const center = new THREE.Vector3(rand(-0.5, 0.5), rand(-0.5, 0.5), rand(-0.5, 0.5));
    for (let q = 0; q < 3 && qCount < nQuarks; q++) {
      const charge = Math.random() < 0.5 ? 2 / 3 : -1 / 3;
      pts.push({
        id: id++,
        type: "quark",
        charge,
        mass: 5,
        pos: center.clone().add(new THREE.Vector3(rand(-0.05, 0.05), rand(-0.05, 0.05), rand(-0.05, 0.05))),
        vel: new THREE.Vector3(rand(-0.001, 0.001), rand(-0.001, 0.001), rand(-0.001, 0.001)),
        mesh: null,
        group: g,
      });
      qCount++;
    }
  }

  return pts;
}

/* ----------------------- Particle Mesh ----------------------- */
function ParticleMesh({ particle }) {
  const meshRef = useRef();
  useEffect(() => {
    particle.mesh = meshRef.current;
  }, [particle]);

  const color =
    particle.type === "electron"
      ? "#00aaff"
      : particle.type === "proton"
      ? "#ff6b6b"
      : "#7cff6b";

  const size =
    particle.type === "electron" ? 0.07 : particle.type === "proton" ? 0.1 : 0.06;

  return (
    <mesh ref={meshRef} position={[particle.pos.x, particle.pos.y, particle.pos.z]}>
      <sphereGeometry args={[size, 24, 24]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.8}
        metalness={0.3}
        roughness={0.3}
      />
    </mesh>
  );
}

/* ----------------------- Physics Engine ----------------------- */
function PhysicsEngine({ particlesRef, mode, coulombK, confinementK, timeStep, drag }) {
  useFrame(() => {
    const particles = particlesRef.current;
    if (!particles || !particles.length) return;

    const forces = particles.map(() => new THREE.Vector3(0, 0, 0));

    // EM forces
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const pi = particles[i];
        const pj = particles[j];
        if (mode === "EM" || mode === "MIXED") {
          const r = new THREE.Vector3().subVectors(pi.pos, pj.pos);
          const dist2 = r.lengthSq() + 0.001;
          const f = r.clone().normalize().multiplyScalar((coulombK * pi.charge * pj.charge) / dist2);
          forces[i].add(f);
          forces[j].sub(f);
        }
      }
    }

    // Strong force on quark groups
    if (mode === "STRONG" || mode === "MIXED") {
      const groups = {};
      for (const p of particles) {
        if (p.type === "quark") {
          const g = p.group ?? 0;
          groups[g] = groups[g] || { center: new THREE.Vector3(), members: [] };
          groups[g].members.push(p);
          groups[g].center.add(p.pos);
        }
      }
      for (const g in groups) {
        const grp = groups[g];
        grp.center.divideScalar(grp.members.length);
        for (const p of grp.members) {
          const r = new THREE.Vector3().subVectors(p.pos, grp.center);
          const dist = r.length();
          const f = r.clone().multiplyScalar(-confinementK * (1 + dist));
          const idx = particles.indexOf(p);
          if (idx >= 0) forces[idx].add(f);
        }
      }
    }

    // integrate
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      const a = forces[i].clone().divideScalar(p.mass);
      p.vel.add(a.multiplyScalar(timeStep));
      p.vel.multiplyScalar(1 - drag * timeStep);
      p.pos.add(p.vel.clone().multiplyScalar(timeStep));

      // bounds
      const bound = 4.5;
      ["x", "y", "z"].forEach((axis) => {
        if (p.pos[axis] > bound || p.pos[axis] < -bound) {
          p.vel[axis] *= -0.6;
          p.pos[axis] = Math.max(-bound, Math.min(bound, p.pos[axis]));
        }
      });

      if (p.mesh) p.mesh.position.copy(p.pos);
    }
  });
  return null;
}

/* ----------------------- Main Component ----------------------- */
export default function ParticleSimulator() {
  const [nElectrons, setNE] = useState(8);
  const [nProtons, setNP] = useState(6);
  const [nQuarks, setNQ] = useState(6);
  const [mode, setMode] = useState("MIXED");
  const [coulombK, setCoulombK] = useState(0.6);
  const [confinementK, setConfinementK] = useState(0.6);
  const [timeStep, setTimeStep] = useState(0.7);
  const [drag, setDrag] = useState(0.08);

  const [particles, setParticles] = useState(
    createParticles({ nElectrons, nProtons, nQuarks })
  );

  const particlesRef = useRef(particles);
  particlesRef.current = particles; // keep ref updated

  const resetParticles = () => {
    const newParticles = createParticles({ nElectrons, nProtons, nQuarks });
    setParticles(newParticles);
  };

  useEffect(() => {
    resetParticles();
  }, [nElectrons, nProtons, nQuarks]);

  return (
    <div className="particle-root">
      <style>{styles}</style>
      <h2>Particle Simulation Lab — 3D</h2>

      <div className="controls">
        <label>
          Electrons: {nElectrons}
          <input
            type="range"
            min="0"
            max="20"
            value={nElectrons}
            onChange={(e) => setNE(Number(e.target.value))}
          />
        </label>
        <label>
          Protons: {nProtons}
          <input
            type="range"
            min="0"
            max="20"
            value={nProtons}
            onChange={(e) => setNP(Number(e.target.value))}
          />
        </label>
        <label>
          Quarks: {nQuarks}
          <input
            type="range"
            min="0"
            max="12"
            value={nQuarks}
            onChange={(e) => setNQ(Number(e.target.value))}
          />
        </label>
        <label>
          Mode:
          <select value={mode} onChange={(e) => setMode(e.target.value)}>
            <option value="EM">Electromagnetic</option>
            <option value="STRONG">Strong</option>
            <option value="MIXED">Mixed</option>
          </select>
        </label>
        <label>
          Coulomb: {coulombK.toFixed(2)}
          <input
            type="range"
            min="0.05"
            max="4"
            step="0.01"
            value={coulombK}
            onChange={(e) => setCoulombK(Number(e.target.value))}
          />
        </label>
        <label>
          Confinement: {confinementK.toFixed(2)}
          <input
            type="range"
            min="0"
            max="3"
            step="0.01"
            value={confinementK}
            onChange={(e) => setConfinementK(Number(e.target.value))}
          />
        </label>
        <label>
          Step: {timeStep.toFixed(2)}
          <input
            type="range"
            min="0.01"
            max="2"
            step="0.01"
            value={timeStep}
            onChange={(e) => setTimeStep(Number(e.target.value))}
          />
        </label>
        <label>
          Drag: {drag.toFixed(2)}
          <input
            type="range"
            min="0"
            max="0.5"
            step="0.01"
            value={drag}
            onChange={(e) => setDrag(Number(e.target.value))}
          />
        </label>
        <button className="button" onClick={resetParticles}>
          Reset
        </button>
      </div>

      <div className="legend">
        <span style={{ color: "#00aaff" }}>Electron</span> •{" "}
        <span style={{ color: "#ff6b6b" }}>Proton</span> •{" "}
        <span style={{ color: "#7cff6b" }}>Quark</span>

      </div>
      <br></br>

      <div className="canvas-wrap">
        <Canvas camera={{ position: [0, 0, 6], fov: 90 }}>
          <ambientLight intensity={0.7} />
          <pointLight position={[10, 10, 10]} intensity={1.0} />
          <OrbitControls enablePan enableZoom enableRotate />
          <axesHelper args={[2]} />

          {particles.map((p) => (
            <ParticleMesh key={p.id} particle={p} />
          ))}

          <PhysicsEngine
            particlesRef={particlesRef}
            mode={mode}
            coulombK={coulombK}
            confinementK={confinementK}
            timeStep={timeStep}
            drag={drag}
          />
        </Canvas>
      </div>

      
    </div>
  );
}
